import Layout from "../layout/Layout";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  useFieldsState,
  useFieldsDispatch,
  fetchFields,
} from "../../context/fields";
import { fetchField, useAnalyticsDispatch } from "../../context/analytics";
import { useAuthState, useAuthDispatch } from "../../context/auth";
import { useSettingsDispatch } from "../../context/settings";
import { refresh } from "../../context/auth";

import { useParams } from "react-router-dom";
import Card from "../cards/Card";
import { SelectInput, TextInput } from "../forms/FormInputs";
import { ConditionCard } from "./automation/ConditionCard";
import { ActionCard } from "./automation/ActionCard";
import {
  Button,
  Typography,
  Divider,
  Box,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import FormButton from "../forms/FormButton";
import {
  Action,
  addAutomation,
  Automation,
  Condition,
  getAutomation,
  updateAutomation,
} from "../../api/automation";
import { useHistory } from "react-router-dom";
import Loader from "../elements/Loader";
import CardWarning from "../cards/CardWarning";
import { AutomationLogTable } from "../tables/AutomationLogTable";

import {
  MetricsProvider,
  fetchMetrics,
  useMetricsDispatch,
} from "../../context/metrics";
import { filterEmpty, refine } from "../../helpers/nullHelpers";
import { useMapState } from "../../helpers/reactiveMap";
import { listLoggers, Logger } from "../../api/loggers";

const AutomationConfig = () => {
  const { id } = useParams() as { id?: string };
  const editing = id !== "new";
  const numericId = id && editing ? parseInt(id) : undefined;

  const user = useAuthState();
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationError, setAutomationError] = useState(false);
  const isLoading = fieldsLoading || automationLoading;
  const history = useHistory();

  const { fields: allFields } = useFieldsState();

  const [fieldId, setFieldId] = useState<number>();

  const fieldsDispatch = useFieldsDispatch();
  const settingsDispatch = useSettingsDispatch();
  const authDispatch = useAuthDispatch();
  const metricsDispatch = useMetricsDispatch();
  const analyticsDispatch = useAnalyticsDispatch();
  const loadFields = useCallback(async () => {
    if (!user.client.id || !user.token) return;
    setFieldsLoading(true);
    try {
      await fetchFields(
        fieldsDispatch,
        user.client.id,
        user.token,
        user.client.directory
      );
      setFieldsLoading(false);
    } catch (error) {
      setFieldsLoading(false);
    }
  }, [
    fieldsDispatch,
    user.client.id,
    user.token,
    user.client.directory,
    settingsDispatch,
    user.settings,
  ]);

  const loadField = useCallback(async () => {
    if (!user.client.id || !user.token || !fieldId) return;
    try {
      fetchField(
        analyticsDispatch,
        user.client.id,
        fieldId,
        user.client.directory,
        user.token
      );
      await fetchMetrics(metricsDispatch, user.token);
    } catch (error) {
      console.error("Error loading field", error);
    }
  }, [fieldId]);

  // Load fields initially
  useEffect(() => {
    if (!allFields.length) {
      const { token, tokenExpiry } = user;
      if (!token) return;
      refresh(authDispatch, { token, tokenExpiry });
      loadFields();
    }
  }, [authDispatch, user, loadFields, allFields, metricsDispatch]);

  useEffect(() => {
    loadField();
  }, [fieldId]);

  const [loggers, setLoggers] = useState<Logger[]>([]);

  const loadLoggers = useCallback(async () => {
    if (!user.token) return;
    try {
      const response = await listLoggers(user.token);
      setLoggers(response.data);
    } catch {}
  }, [user.token]);
  // Load loggers initially
  useEffect(() => {
    if (!loggers.length && user.token) {
      loadLoggers();
    }
  }, [loadLoggers, loggers, user.token]);

  const fieldOptions = useMemo(
    () => Object.fromEntries(allFields.map((field) => [field.id, field.name])),
    [allFields]
  );

  const [name, setName] = useState("");
  const conditions = useMapState<number, Condition | null>(
    new Map([[0, { type: "time", start: "07:00", end: "18:00" }]])
  );
  const actions = useMapState<number, Action | null>();
  const [enabled, setEnabled] = useState(true);
  const [isOr, setIsOr] = useState(false);

  // Load existing automation if in edit mode
  useEffect(() => {
    if (!user.token || !user.client.id) return;
    if (numericId !== undefined) {
      setAutomationLoading(true);
      setAutomationError(false);
      getAutomation(user.token, user.client.id, numericId)
        .then((response) => {
          setFieldId(response.data.field_id);
          setName(response.data.name);
          const cs = response.data.data.conditions;
          if (cs.length === 2 && cs[1].type === "or") {
            setIsOr(true);
            conditions.reset([
              [0, cs[0]],
              ...cs[1].conditions.map(
                (condition, idx) => [idx + 1, condition] as const
              ),
            ]);
            setNextConditionId(cs[1].conditions.length + 1);
          } else {
            conditions.reset(
              cs.map(
                (condition: Condition, idx: number) => [idx, condition] as const
              )
            );
            setNextConditionId(cs.length);
          }
          actions.reset(
            response.data.data.actions.map(
              (action, idx) => [idx, action] as const
            )
          );
          setNextActionId(response.data.data.actions.length);
          setAutomationLoading(false);
          setEnabled(!response.data.data.disabled);
        })
        .catch(() => {
          setAutomationError(true);
          setAutomationLoading(false);
        });
    }
  }, [numericId, user]);

  const currentAutomation = useMemo<
    Omit<Automation, "id" | "field_id"> & Partial<Automation>
  >(
    () => ({
      id: numericId,
      field_id: fieldId,
      name: name,
      invalid: false,
      data: {
        disabled: !enabled,
        conditions: isOr
          ? [
              conditions.value.get(0)!,
              {
                type: "or" as const,
                conditions: refine(conditions.value.entries(), ([i, c]) =>
                  i ? c : undefined
                ),
              },
            ]
          : filterEmpty(conditions.value.values()),
        actions: filterEmpty(actions.value.values()),
      },
    }),
    [fieldId, name, conditions.value, actions.value, enabled, isOr]
  );

  const validAutomation = useMemo(() => {
    if (
      currentAutomation.field_id !== undefined &&
      currentAutomation.name &&
      currentAutomation.data.conditions.length &&
      currentAutomation.data.conditions.every(
        (c) => !("conditions" in c) || c.conditions.length
      ) &&
      currentAutomation.data.actions.length
    ) {
      return currentAutomation as Omit<Automation, "id">;
    }
  }, [currentAutomation]);

  const [nextConditionId, setNextConditionId] = useState(1);
  const [nextActionId, setNextActionId] = useState(0);

  const fieldInput = {
    value: fieldId?.toString() ?? "",
    onChange: (evt: any) => {
      setFieldId(evt.target.value ? parseInt(evt.target.value) : undefined);
    },
  };

  const targetField = useMemo(
    () => allFields.find((field) => field.id === fieldId),
    [allFields, fieldId]
  );

  const relevantLoggers = useMemo(() => {
    if (!fieldId)
      return loggers.filter((l) =>
        l.fields?.some((f) => f.client_id === user.client.id)
      );
    return loggers.filter((l) => l.fields?.some((f) => f.id === fieldId));
  }, [loggers, fieldId, user.client.id]);

  const nameInput = {
    value: name,
    onChange: (evt: any) => {
      setName(evt.target.value);
    },
  };

  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (validAutomation && user.token && user.client.id) {
      setSubmitting(true);
      if (numericId !== undefined) {
        await updateAutomation(user.token, user.client.id, {
          ...validAutomation,
          id: numericId,
        });
      } else {
        await addAutomation(user.token, user.client.id, validAutomation);
      }
      history.push("/automation");
    }
  }, [validAutomation, user]);

  const cancel = useCallback(() => {
    history.push("/automation");
  }, [history]);

  return (
    <Layout>
      <Loader
        active={isLoading}
        showChildren={false}
        message="Loading automation..."
      >
        {automationError ? (
          <CardWarning
            message={`Automation #${id} not found`}
            color="disabled"
          />
        ) : (
          <>
            {editing && (
              <Box mb={3}>
                <AutomationLogTable automation_id={id} />
              </Box>
            )}
            <Card
              headline={`${id === "new" ? "Add" : "Update"} Automation`}
              headlineVariant="h1"
            >
              <TextInput label="Name" input={nameInput} meta={{}} />
              <SelectInput
                id="target-field"
                label="Target Field"
                input={fieldInput}
                options={fieldOptions}
                meta={{}}
              />

              <FormControlLabel
                control={
                  <Switch
                    name="Enabled"
                    checked={enabled}
                    onChange={(evt, checked) => setEnabled(checked)}
                    color="primary"
                  />
                }
                label={enabled ? "Enabled" : "Disabled"}
              />

              <Box mt={2} mb={2}>
                <Divider />
              </Box>
              <Typography variant="h5">Conditions</Typography>
              {[...conditions.value.entries()].map(([id, condition]) => (
                <>
                  <ConditionCard
                    key={id}
                    field={allFields.find((f) => f.id === fieldId)}
                    user={user}
                    fieldId={fieldId}
                    loggers={relevantLoggers}
                    onChange={(value) => {
                      if (value) {
                        conditions.set(id, value);
                      }
                    }}
                    onRemove={() => {
                      conditions.delete(id);
                    }}
                    value={condition}
                  />
                  {!id && conditions.value.size > 1 && (
                    <Typography style={{ marginLeft: 16 }} key={id}>
                      And{" "}
                      <Select
                        label="And/Or"
                        value={isOr ? "or" : "and"}
                        onChange={(e) => {
                          setIsOr(e.target.value === "or");
                        }}
                      >
                        <MenuItem value="and">all of the below (AND)</MenuItem>
                        <MenuItem value="or">some of the below (OR)</MenuItem>
                      </Select>
                    </Typography>
                  )}
                </>
              ))}
              <Button
                onClick={() => {
                  conditions.set(nextConditionId, null);
                  setNextConditionId(nextConditionId + 1);
                }}
                variant="contained"
                color="primary"
              >
                Create New Condition
              </Button>
              <Box mt={2} mb={2}>
                <Divider />
              </Box>
              <Typography variant="h5">Actions</Typography>
              {[...actions.value.entries()].map(([id, action]) => (
                <ActionCard
                  key={id}
                  onChange={(value) => {
                    if (!value) return;
                    actions.set(id, value);
                  }}
                  onRemove={() => {
                    actions.delete(id);
                  }}
                  value={action}
                  targetField={targetField}
                />
              ))}
              <Button
                onClick={() => {
                  actions.set(nextActionId, null);
                  setNextActionId(nextActionId + 1);
                }}
                variant="contained"
                color="primary"
              >
                Create New Action
              </Button>

              <Box mt={2} mb={2}>
                <Divider />
              </Box>

              <FormButton
                handleClick={cancel}
                variant="contained"
                text="Cancel"
              />
              <FormButton
                disabled={!validAutomation}
                handleClick={submit}
                variant="contained"
                color="primary"
                text={editing ? "Update Automation" : "Create Automation"}
                waiting={submitting}
              />
            </Card>
          </>
        )}
      </Loader>
    </Layout>
  );
};

export const AutomationConfigPage = () => (
  <MetricsProvider>
    <AutomationConfig />
  </MetricsProvider>
);
